import { supabase } from '@/integrations/supabase/client';

export interface BookOverride {
  book_key: string;
  views: number;
  clicks: number;
  engagement_rate: number;
  title: string | null;
  genre: string | null;
  description: string | null;
  cover_image_url: string | null;
}

export type OverridesMap = Map<string, BookOverride>;

// ----- Module-level cache (shared across pages, dedupes in-flight requests) -----
let _bookOvCache: OverridesMap | null = null;
let _bookOvPromise: Promise<OverridesMap> | null = null;
let _authorOvCache: AuthorOverridesMap | null = null;
let _authorOvPromise: Promise<AuthorOverridesMap> | null = null;

export function invalidateBookOverridesCache() {
  _bookOvCache = null;
  _bookOvPromise = null;
}
export function invalidateAuthorOverridesCache() {
  _authorOvCache = null;
  _authorOvPromise = null;
}

async function _loadBookOverrides(): Promise<OverridesMap> {
  const { data } = await supabase
    .from('book_metric_overrides')
    .select('book_key, views, clicks, engagement_rate, title, genre, description, cover_image_url');
  const map: OverridesMap = new Map();
  (data || []).forEach((row: any) => {
    map.set(row.book_key, {
      book_key: row.book_key,
      views: Number(row.views) || 0,
      clicks: Number(row.clicks) || 0,
      engagement_rate: Number(row.engagement_rate) || 0,
      title: row.title ?? null,
      genre: row.genre ?? null,
      description: row.description ?? null,
      cover_image_url: row.cover_image_url ?? null,
    });
  });
  return map;
}

export async function fetchOverrides(force = false): Promise<OverridesMap> {
  if (!force && _bookOvCache) return _bookOvCache;
  if (!force && _bookOvPromise) return _bookOvPromise;
  _bookOvPromise = _loadBookOverrides().then((m) => {
    _bookOvCache = m;
    _bookOvPromise = null;
    return m;
  }).catch((e) => { _bookOvPromise = null; throw e; });
  return _bookOvPromise;
}

/** Synchronously read cached overrides (empty map if not loaded yet). */
export function getCachedOverrides(): OverridesMap {
  return _bookOvCache ?? new Map();
}

/** Apply metric + content overrides to a book object (uses book.id as the key). */
export function applyOverride<T extends {
  id: string;
  title?: string;
  genre?: string;
  description?: string;
  coverImage?: string;
  readers?: number;
  clicks?: number;
  engagementRate?: number;
}>(book: T, overrides: OverridesMap): T {
  const o = overrides.get(book.id);
  if (!o) return book;
  return {
    ...book,
    readers: o.views,
    clicks: o.clicks,
    engagementRate: o.engagement_rate,
    ...(o.title ? { title: o.title } : {}),
    ...(o.genre ? { genre: o.genre } : {}),
    ...(o.description ? { description: o.description } : {}),
    ...(o.cover_image_url ? { coverImage: o.cover_image_url } : {}),
  };
}

// ============================================================
// Author overrides (static catalog authors)
// ============================================================

export interface AuthorOverride {
  author_key: string;
  name: string | null;
  bio: string | null;
  country: string | null;
  image_url: string | null;
}

export type AuthorOverridesMap = Map<string, AuthorOverride>;

async function _loadAuthorOverrides(): Promise<AuthorOverridesMap> {
  const { data } = await supabase
    .from('author_overrides')
    .select('author_key, name, bio, country, image_url');
  const map: AuthorOverridesMap = new Map();
  (data || []).forEach((row: any) => {
    map.set(row.author_key, {
      author_key: row.author_key,
      name: row.name ?? null,
      bio: row.bio ?? null,
      country: row.country ?? null,
      image_url: row.image_url ?? null,
    });
  });
  return map;
}

export async function fetchAuthorOverrides(force = false): Promise<AuthorOverridesMap> {
  if (!force && _authorOvCache) return _authorOvCache;
  if (!force && _authorOvPromise) return _authorOvPromise;
  _authorOvPromise = _loadAuthorOverrides().then((m) => {
    _authorOvCache = m;
    _authorOvPromise = null;
    return m;
  }).catch((e) => { _authorOvPromise = null; throw e; });
  return _authorOvPromise;
}

export function getCachedAuthorOverrides(): AuthorOverridesMap {
  return _authorOvCache ?? new Map();
}

// Subscribe once to realtime invalidation so any change refreshes the cache for everyone.
let _realtimeWired = false;
export function wireOverridesRealtime() {
  if (_realtimeWired) return;
  _realtimeWired = true;
  supabase
    .channel('overrides-cache-invalidation')
    .on('postgres_changes', { event: '*', schema: 'public', table: 'book_metric_overrides' }, () => {
      invalidateBookOverridesCache();
      fetchOverrides();
    })
    .on('postgres_changes', { event: '*', schema: 'public', table: 'author_overrides' }, () => {
      invalidateAuthorOverridesCache();
      fetchAuthorOverrides();
    })
    .subscribe();
}

export function applyAuthorOverride<T extends {
  id: string;
  name?: string;
  bio?: string;
  country?: string;
  image?: string;
}>(author: T, overrides: AuthorOverridesMap): T {
  const o = overrides.get(author.id);
  if (!o) return author;
  return {
    ...author,
    ...(o.name ? { name: o.name } : {}),
    ...(o.bio ? { bio: o.bio } : {}),
    ...(o.country ? { country: o.country } : {}),
    ...(o.image_url ? { image: o.image_url } : {}),
  };
}
